import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { countSuppressedContacts } from '../suppression-api.js';
import { prepareBody } from './logic/save-payload.js';
import {
  canFinish,
  channelFallbacksFor,
  coverageGaps as coverageGapsOf,
  fallbackCandidates as fallbackCandidatesOf,
  gapKey,
  leadsWithoutEmail,
  persistedFallbackChoices,
  repName,
  unresolvedGaps as unresolvedGapsOf,
  validateDraft,
} from './logic/summary.js';
import type { CampaignDraft, SalesRep } from './types.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface WizSummaryProps {
  draft: CampaignDraft;
  /**
   * Called after the prepare endpoint accepts the request. The wizard host should
   * navigate to the Campaign Detail page where the user reviews/edits the
   * generated messages and triggers the actual launch.
   */
  onPrepared?: (campaignId: string) => void;
}

type PrepareStatus = 'idle' | 'submitting' | 'error';

// ---------------------------------------------------------------------------
// Validation: the checklist and the coverage gaps are logic/summary.ts's.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const WizSummary = stateful((props: WizSummaryProps) => {
  const { draft, onPrepared } = props;

  const [prepareStatus, setPrepareStatus] = useState<PrepareStatus>('idle');
  const [prepareError, setPrepareError] = useState('');
  const [acknowledgedCoverage, setAcknowledgedCoverage] = useState(false);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  /**
   * Per-gap fallback selection. Key = `${sourceAieId}::${channel}`,
   * value = fallback Sales Rep id (or '' for "no fallback — silently skip").
   */
  const [fallbackChoices, setFallbackChoices] = useState<Record<string, string>>({});

  // Seed the dropdowns from fallbacks persisted on the campaign (edit of a
  // prepared campaign / duplicated campaign). Without this the warning always
  // reset to "Skip" even though the draft carries channelFallbacks.
  useEffect(() => {
    const persisted = persistedFallbackChoices(draft);
    if (Object.keys(persisted).length === 0) return;
    // User picks win over persisted values.
    setFallbackChoices({ ...persisted, ...fallbackChoices });
  }, [JSON.stringify(draft.channelFallbacks ?? [])]);

  // Lazy-load Sales Rep list so we can show readable names in the coverage warning.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch('/api/ai-employees');
        const data = (await resp.json()) as { ok: boolean; aiEmployees?: SalesRep[] };
        if (!cancelled && data.ok) setSalesReps(data.aiEmployees ?? []);
      } catch {
        /* ignore — names will fall back to Sales Rep id */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const salesRepName = (id: string): string => repName(salesReps, id);

  // ── Coverage gaps (multi-mode only) ─────────────────────────────────────
  // For each Sales Rep that has at least one channel but is missing the other,
  // which channel they cannot serve and how many leads (estimated by their
  // percentage share) will skip that channel (logic/summary.ts).
  const coverageGaps = coverageGapsOf(draft);

  const checks = validateDraft(draft);
  const allValid = checks.every(c => c.ok);

  // ── Suppression check: how many leads are on the Do-Not-Contact list? ──
  // POST /api/suppression/count (R11c): the number only. A count that cannot be
  // read hides the hint, as the BaseQL query did when it failed.
  const leadEmails = draft.leads.map(l => l.email).filter(Boolean) as string[];
  const [suppressedCount, setSuppressedCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    countSuppressedContacts(leadEmails).then(
      n => {
        if (!cancelled) setSuppressedCount(n);
      },
      () => {
        if (!cancelled) setSuppressedCount(0);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [draft.leads.length]);

  // The channelFallbacks payload from the user's per-gap dropdown picks; gaps
  // NOT covered by a fallback still require explicit acknowledgement.
  const channelFallbacks = channelFallbacksFor(coverageGaps, fallbackChoices);
  const unresolvedGaps = unresolvedGapsOf(coverageGaps, fallbackChoices);
  const requiresAcknowledgement = unresolvedGaps.length > 0;
  const canGenerate = canFinish({
    checks,
    submitting: prepareStatus === 'submitting',
    needsAcknowledgement: requiresAcknowledgement,
    acknowledged: acknowledgedCoverage,
  });

  // Which Sales Reps can serve as fallback for a given missing channel?
  const fallbackCandidates = (missing: 'email' | 'linkedin', sourceSalesRepId: string): string[] =>
    fallbackCandidatesOf(draft, missing, sourceSalesRepId);

  // ── Generate Messages (Phase A) ─────────────────────────────────────────
  const generateMessages = async () => {
    if (prepareStatus === 'submitting') return;
    setPrepareStatus('submitting');
    setPrepareError('');

    try {
      const resp = await fetch('/api/campaign/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prepareBody(draft, channelFallbacks)),
      });

      const data = (await resp.json()) as { ok: boolean; campaignId?: string; error?: string };
      if (!data.ok || !data.campaignId) {
        setPrepareStatus('error');
        setPrepareError(data.error ?? 'Prepare failed');
        return;
      }
      onPrepared?.(data.campaignId);
    } catch (e) {
      setPrepareStatus('error');
      setPrepareError(e instanceof Error ? e.message : 'Network error');
    }
  };

  // ── Render Helpers ──────────────────────────────────────────────────────
  const campaignChecklist = () => html`
    <div class="card bg-base-100">
      <div class="card-body p-6">
        <div class="flex items-center gap-3">
          <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
            <span class="icon-[material-symbols--summarize] text-xl"></span>
          </span>
          <h2 class="text-[0.9375rem] font-medium">Campaign summary</h2>
        </div>
        <div class="mt-2 flex flex-col gap-1.5">
          ${checks.map(
            c => html`
              <div class="flex items-center gap-2 text-sm">
                <span
                  class="${
                    // Done in ink, as on the lead timeline; what is missing stays red.
                    c.ok
                      ? 'icon-[material-symbols--check-circle] text-base-content/50'
                      : 'icon-[material-symbols--cancel] text-error'
                  } shrink-0 text-lg"
                ></span>
                <span class="font-medium">${c.label}:</span>
                <span class="${c.ok ? 'text-base-content/70' : 'text-error/70'}">${c.detail}</span>
              </div>
            `,
          )}
        </div>
        ${
          !allValid
            ? html`<div class="alert alert-warning mt-3 text-sm">
              <span class="icon-[material-symbols--warning]"></span>
              Please complete all steps before finishing setup.
            </div>`
            : nothing
        }
      </div>
    </div>
  `;

  const generateSection = () => {
    return html`
      <div class="card bg-base-100">
        <div class="card-body p-6">
          <h2 class="text-[0.9375rem] font-medium">Finish setup</h2>

          ${
            prepareStatus === 'error'
              ? html`
                <div class="alert alert-error text-sm">
                  <span class="icon-[material-symbols--error]"></span>
                  ${prepareError}
                </div>
              `
              : nothing
          }

          <p class="text-base-content/70 text-sm">
            Please review your campaign configuration above. If you're happy with everything, complete the setup to
            switch to the campaign dashboard where you can launch the campaign.
          </p>

          <div class="card-actions mt-4 justify-end">
            <button class="btn btn-primary gap-1" ?disabled=${!canGenerate} @click=${() => void generateMessages()}>
              <span class="icon-[material-symbols--check-circle]"></span> Finish Setup
            </button>
          </div>
        </div>
      </div>
    `;
  };

  const coverageWarning = () => {
    if (coverageGaps.length === 0) return nothing;
    return html`
      <div class="card bg-base-100">
        <div class="card-body gap-3 p-6">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--warning] text-warning text-xl"></span>
            <h2 class="text-[0.9375rem] font-medium">Channel coverage warning</h2>
          </div>
          <p class="text-base-content/80 text-sm">
            In Cross-Channel campaigns each lead is permanently assigned to exactly one Sales Representative and only
            ever receives messages from that Sales Representative's accounts. Some of your Sales Representatives are
            missing a channel — choose another Sales Representative's account to send those steps, or accept that the
            steps will be silently skipped.
          </p>
          <ul class="space-y-2">
            ${coverageGaps.map(g => {
              const key = gapKey(g);
              const channelLabel = g.missing === 'email' ? 'Email' : 'LinkedIn';
              const candidates = fallbackCandidates(g.missing, g.aiEmployeeId);
              const selected = fallbackChoices[key] ?? '';
              return html`
                <li class="bg-base-200 rounded-lg p-3 text-sm">
                  <div class="font-medium">${salesRepName(g.aiEmployeeId)}</div>
                  <div class="text-base-content/70 mt-0.5">
                    No <strong>${channelLabel}</strong> account configured — approx.
                    <strong>${g.estimatedLeads}</strong> lead${g.estimatedLeads === 1 ? '' : 's'} (${g.percentage}%
                    share) affected.
                  </div>
                  ${
                    candidates.length > 0
                      ? html`
                        <div class="mt-2 flex flex-wrap items-center gap-2">
                          <label class="text-base-content/70 text-xs"> Send ${channelLabel} steps via: </label>
                          <select
                            class="select select-bordered select-xs"
                            .value=${selected}
                            @change=${(e: Event) => {
                              const value = (e.target as HTMLSelectElement).value;
                              setFallbackChoices({ ...fallbackChoices, [key]: value });
                              if (value) setAcknowledgedCoverage(false);
                            }}
                          >
                            <option value="">— Skip ${channelLabel} steps for these leads —</option>
                            ${candidates.map(
                              c => html`
                                <option value=${c} ?selected=${selected === c}>
                                  ${salesRepName(c)}'s ${channelLabel} account
                                </option>
                              `,
                            )}
                          </select>
                          ${
                            selected
                              ? html`
                                <span class="text-base-content/70 text-xs">
                                  <span class="icon-[material-symbols--check-circle] text-base-content/50 align-text-bottom"></span>
                                  Identity stays ${salesRepName(g.aiEmployeeId)} — sending account taken from
                                  ${salesRepName(selected)}.
                                </span>
                              `
                              : nothing
                          }
                        </div>
                      `
                      : html`
                        <div class="text-base-content/60 mt-1 text-xs">
                          No other Sales Representative has a ${channelLabel} account configured — these steps will be
                          skipped.
                        </div>
                      `
                  }
                </li>
              `;
            })}
          </ul>
          ${
            requiresAcknowledgement
              ? html`
                <label class="label mt-1 cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-warning checkbox-sm"
                    .checked=${acknowledgedCoverage}
                    @change=${(e: Event) => setAcknowledgedCoverage((e.target as HTMLInputElement).checked)}
                  />
                  <span class="label-text text-sm">
                    I understand that some leads will not receive all channels (no fallback chosen).
                  </span>
                </label>
              `
              : html`
                <div class="alert alert-success text-xs">
                  <span class="icon-[material-symbols--check-circle]"></span>
                  All coverage gaps are covered by a fallback Sales Rep account. Ready to generate.
                </div>
              `
          }
        </div>
      </div>
    `;
  };

  const linkedinSafetyInfo = () => {
    if (draft.campaignType !== 'linkedin') return nothing;
    return html`
      <div class="card bg-base-100">
        <div class="card-body gap-3 p-6">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--shield-lock] text-info text-xl"></span>
            <h2 class="text-[0.9375rem] font-medium">LinkedIn account protection</h2>
          </div>
          <p class="text-base-content/70 text-sm">
            To prevent your LinkedIn account from being flagged or banned, all outreach actions are sent through a
            safety engine with the following measures:
          </p>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div class="bg-base-200 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-medium">
                <span class="icon-[material-symbols--speed] text-base-content/50"></span>
                Daily Limits
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>Max 20 connection requests/day</li>
                <li>Max 50 DMs/day</li>
                <li>Max 80 total actions/day</li>
                <li>New accounts: reduced limits for first 2 weeks</li>
              </ul>
            </div>
            <div class="bg-base-200 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-medium">
                <span class="icon-[material-symbols--timer] text-base-content/50"></span>
                Human-like Delays
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>15–180s pause between each lead</li>
                <li>5–15 min break every 8–15 actions</li>
                <li>Min. 20 min between sequence steps</li>
                <li>Randomized timing (no fixed patterns)</li>
              </ul>
            </div>
            <div class="bg-base-200 rounded-lg p-3">
              <div class="flex items-center gap-1.5 text-sm font-medium">
                <span class="icon-[material-symbols--emergency-home] text-base-content/50"></span>
                Auto Circuit Breaker
              </div>
              <ul class="text-base-content/70 mt-1.5 space-y-0.5 text-xs">
                <li>1 rate-limit warning → 1h pause</li>
                <li>2 warnings/day → stop until next day</li>
                <li>Only sends during your business hours</li>
                <li>Auto-retry with increasing backoff</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Non-blocking warning: the sequence has an email step but some leads have no
  // email address (e.g. LinkedIn-only imports). Those leads still receive the
  // LinkedIn steps; they are simply skipped for the email step(s). No warning is
  // shown when there is no email step (LinkedIn-only campaigns are fully fine).
  const emailCoverageWarning = () => {
    const missingEmailCount = leadsWithoutEmail(draft);
    if (missingEmailCount === 0) return nothing;
    const total = draft.leads.length;
    return html`
      <div class="card bg-base-100">
        <div class="card-body gap-2 p-6">
          <div class="flex items-center gap-2">
            <span class="icon-[material-symbols--warning] text-warning text-xl"></span>
            <h2 class="text-[0.9375rem] font-medium">Some leads have no email address</h2>
          </div>
          <p class="text-base-content/80 text-sm">
            <strong>${missingEmailCount}</strong> of <strong>${total}</strong>
            lead${total === 1 ? '' : 's'} have no email address, but your sequence contains an email step. These leads
            will be skipped for the email step(s) — they still receive the LinkedIn steps when a LinkedIn URL is
            present. Remove the email step or add email addresses if every lead should receive it.
          </p>
        </div>
      </div>
    `;
  };

  return html`
    <div class="flex flex-col gap-4">
      ${campaignChecklist()} ${coverageWarning()} ${emailCoverageWarning()} ${linkedinSafetyInfo()}
      ${
        suppressedCount > 0
          ? html`
            <div class="card bg-base-100">
              <div class="card-body p-6">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--block] text-info text-xl"></span>
                  <h2 class="text-[0.9375rem] font-medium">Suppressed contacts</h2>
                </div>
                <p class="text-base-content/70 text-sm">
                  <strong>${suppressedCount}</strong> contact${suppressedCount === 1 ? '' : 's'} on this campaign's lead
                  list have opted out of all outreach. They stay on the list, marked opted out — no message is generated
                  or sent to them.
                </p>
              </div>
            </div>
          `
          : nothing
      }
      ${generateSection()}
    </div>
  `;
});
